import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

type Channel = "email" | "sms";
type Audience = "customer" | "crew";
type SendState = "idle" | "sending" | "success" | "error";

type ProviderStatus = {
  email: { configured: boolean; provider: string; customSender: boolean; missing: string[] };
  sms: { configured: boolean; provider: string; missing: string[] };
};

type Template = {
  id: string;
  label: string;
  subject: string;
  body: (ctx: { name: string; projectId: string; moveType: string; date: string }) => string;
};

const TEMPLATES: Template[] = [
  {
    id: "reminder",
    label: "Day-before reminder",
    subject: "Reminder: your move is tomorrow",
    body: ({ name, date }) =>
      `Hi ${name.split(" ")[0]},\n\nJust a quick reminder that your move is scheduled for ${date}. Our crew will arrive in the time window we discussed.\n\nPlease have:\n- All boxes labeled and sealed\n- Walkways clear\n- Pets in a safe spot\n\nReply to this message if anything changes. See you soon!\n\n— Haulmark Dispatch`,
  },
  {
    id: "on-the-way",
    label: "Crew on the way",
    subject: "Your Haulmark crew is on the way",
    body: ({ name }) =>
      `Hi ${name.split(" ")[0]},\n\nYour Haulmark crew has just left and is on the way to your origin address. ETA is within the next 20-30 minutes.\n\nIf you need to reach the lead mover directly, just reply here and we'll patch you through.\n\n— Haulmark Dispatch`,
  },
  {
    id: "delay",
    label: "Slight delay",
    subject: "Quick update on your move time",
    body: ({ name }) =>
      `Hi ${name.split(" ")[0]},\n\nWe're running a little behind schedule today and wanted to give you a heads up. We now expect to arrive about 30-45 minutes after the original window.\n\nReally sorry for the shuffle — we'll text again the moment we're 10 minutes out.\n\n— Haulmark Dispatch`,
  },
  {
    id: "balance-due",
    label: "Balance due reminder",
    subject: "Final balance for your upcoming move",
    body: ({ name, projectId }) =>
      `Hi ${name.split(" ")[0]},\n\nThis is a friendly reminder that the balance for project ${projectId} is due on move day. You can pay by card, ACH, or cash directly with the crew lead.\n\nLet us know if you have any questions before the move!\n\n— Haulmark Dispatch`,
  },
  {
    id: "thanks",
    label: "Thanks + review ask",
    subject: "Thanks for choosing Haulmark!",
    body: ({ name }) =>
      `Hi ${name.split(" ")[0]},\n\nIt was a pleasure helping with your move today! If everything went well, we'd love it if you took 30 seconds to leave a quick review — it really helps our crews.\n\nThanks again for trusting us with the heavy lifting.\n\n— Haulmark Dispatch`,
  },
];

type Recipient = {
  name: string;
  email?: string;
  phone?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  audience: Audience;
  recipient: Recipient;
  projectId: string;
  moveType: string;
  date: string;
};

export default function ContactModal({
  open,
  onClose,
  audience,
  recipient,
  projectId,
  moveType,
  date,
}: Props) {
  const [channel, setChannel] = useState<Channel>(
    recipient.phone ? "sms" : "email",
  );
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipientField, setRecipientField] = useState("");
  const [state, setState] = useState<SendState>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderStatus | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<string>("reminder");

  // Apply template when opening or audience changes
  useEffect(() => {
    if (!open) return;
    const tpl = TEMPLATES.find((t) => t.id === activeTemplate) || TEMPLATES[0];
    setSubject(tpl.subject);
    setBody(
      tpl.body({ name: recipient.name, projectId, moveType, date }),
    );
  }, [open, activeTemplate, recipient.name, projectId, moveType, date]);

  // Pre-fill recipient field
  useEffect(() => {
    if (!open) return;
    setRecipientField(channel === "sms" ? recipient.phone || "" : recipient.email || "");
    setState("idle");
    setErrorMsg(null);
  }, [open, channel, recipient.phone, recipient.email]);

  // Fetch provider status once when opened
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    supabase.functions
      .invoke("mover-link-providers-status", { body: {} })
      .then(({ data }) => {
        if (!cancelled && data) setProviders(data as ProviderStatus);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const charCount = body.length;
  const smsTooLong = channel === "sms" && charCount > 320;

  const providerReady = useMemo(() => {
    if (!providers) return null;
    return channel === "sms" ? providers.sms.configured : providers.email.configured;
  }, [providers, channel]);

  if (!open) return null;

  const close = () => {
    if (state === "sending") return;
    onClose();
  };

  const send = async () => {
    if (!recipientField.trim() || !body.trim()) {
      setErrorMsg("Recipient and message are required.");
      return;
    }
    if (channel === "email" && !subject.trim()) {
      setErrorMsg("Subject is required for email.");
      return;
    }
    setState("sending");
    setErrorMsg(null);
    try {
      const fnName = channel === "sms" ? "send-customer-sms" : "send-customer-email";
      const payload =
        channel === "sms"
          ? { to: recipientField, message: body }
          : {
              to: recipientField,
              subject,
              message: body,
              recipientName: recipient.name,
              projectId,
            };
      const { data, error } = await supabase.functions.invoke(fnName, { body: payload });
      if (error || (data && data.error)) {
        const msg = (data && data.error) || error?.message || "Failed to send";
        await logMessage("failed", undefined, msg);
        setErrorMsg(msg);
        setState("error");
        return;
      }
      const providerId = data?.id || data?.sid;
      await logMessage("sent", providerId);
      setState("success");
      setTimeout(() => {
        onClose();
        setState("idle");
      }, 1400);
    } catch (e) {
      const msg = (e as Error).message || "Network error";
      await logMessage("failed", undefined, msg);
      setErrorMsg(msg);
      setState("error");
    }
  };

  const logMessage = async (
    status: "sent" | "failed",
    providerId?: string,
    errorMessage?: string,
  ) => {
    const { data: sess } = await supabase.auth.getSession();
    await supabase.from("project_messages").insert({
      project_id: projectId,
      channel,
      audience,
      recipient_name: recipient.name,
      recipient: recipientField,
      subject: channel === "email" ? subject : null,
      body,
      status,
      provider_id: providerId ?? null,
      error_message: errorMessage ?? null,
      sent_by: sess.session?.user.id ?? null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={close}
    >
      <div
        className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 border-b border-ink-100 flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-ink-900">
              Message {audience === "customer" ? "customer" : "crew"}
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">
              {recipient.name} · Project {projectId}
            </p>
          </div>
          <button
            onClick={close}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Channel toggle */}
          <div className="inline-flex bg-cream-100 rounded-md p-1">
            <button
              onClick={() => setChannel("sms")}
              disabled={!recipient.phone}
              className={`text-xs font-bold px-3 py-1.5 rounded-md cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                channel === "sms"
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-700"
              }`}
            >
              <i className="ri-message-2-line mr-1"></i>
              Text (SMS)
            </button>
            <button
              onClick={() => setChannel("email")}
              disabled={!recipient.email}
              className={`text-xs font-bold px-3 py-1.5 rounded-md cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                channel === "email"
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-700"
              }`}
            >
              <i className="ri-mail-line mr-1"></i>
              Email
            </button>
          </div>

          {/* Provider warning */}
          {providerReady === false && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2.5 text-[11px] leading-relaxed">
              <div className="font-semibold flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>
                {channel === "sms" ? "Twilio" : "Resend"} not connected yet
              </div>
              <div className="mt-0.5">
                {channel === "sms"
                  ? "Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_FROM in Supabase secrets."
                  : "Add RESEND_API_KEY in Supabase secrets."}{" "}
                You can still draft the message — sending will fail until secrets are saved.
              </div>
            </div>
          )}

          {/* Templates */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mb-1.5">
              Templates
            </div>
            <div className="flex flex-wrap gap-1.5">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTemplate(t.id)}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                    activeTemplate === t.id
                      ? "bg-ink-900 text-white"
                      : "bg-cream-100 text-ink-700 hover:bg-ink-100"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Recipient field */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mb-1 block">
              To
            </label>
            <input
              type={channel === "email" ? "email" : "tel"}
              value={recipientField}
              onChange={(e) => setRecipientField(e.target.value)}
              placeholder={
                channel === "email" ? "name@example.com" : "+1 512 555 0142"
              }
              className="w-full text-sm px-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white"
            />
            {channel === "sms" && (
              <div className="text-[10px] text-ink-400 mt-1">
                US numbers without country code will be auto-prefixed with +1.
              </div>
            )}
          </div>

          {/* Subject (email only) */}
          {channel === "email" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mb-1 block">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full text-sm px-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white"
              />
            </div>
          )}

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                Message
              </label>
              <span
                className={`text-[10px] tabular-nums ${
                  smsTooLong ? "text-rose-600 font-bold" : "text-ink-400"
                }`}
              >
                {charCount} {channel === "sms" ? "/ 320" : "chars"}
              </span>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              className="w-full text-sm px-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white resize-none leading-relaxed"
            />
            {smsTooLong && (
              <div className="text-[11px] text-rose-600 mt-1">
                Twilio will split this into multiple segments. Consider trimming.
              </div>
            )}
          </div>

          {errorMsg && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 text-[11px] rounded-md p-2.5 leading-relaxed">
              <span className="font-semibold">Failed:</span> {errorMsg}
            </div>
          )}
        </div>

        <div className="px-5 pb-5 pt-3 border-t border-ink-100 flex items-center justify-between gap-2 bg-cream-50 rounded-b-xl">
          <button
            onClick={close}
            disabled={state === "sending"}
            className="text-sm font-semibold text-ink-600 hover:text-ink-900 px-3 py-2 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={state === "sending" || state === "success"}
            className={`inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-md cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed ${
              state === "success"
                ? "bg-emerald-500 text-white"
                : state === "error"
                ? "bg-rose-600 hover:bg-rose-700 text-white"
                : "bg-ink-900 hover:bg-ink-800 text-white"
            }`}
          >
            <i
              className={
                state === "sending"
                  ? "ri-loader-4-line animate-spin"
                  : state === "success"
                  ? "ri-check-double-line"
                  : state === "error"
                  ? "ri-refresh-line"
                  : channel === "sms"
                  ? "ri-send-plane-line"
                  : "ri-mail-send-line"
              }
            ></i>
            {state === "sending"
              ? "Sending…"
              : state === "success"
              ? "Sent"
              : state === "error"
              ? "Retry"
              : channel === "sms"
              ? "Send text"
              : "Send email"}
          </button>
        </div>
      </div>
    </div>
  );
}